import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { DataCollector } from '../types/admin';
import { 
  validateOptionalEmail, 
  validateOptionalContactPhoneNumber 
} from '../utils/contactValidation';
import {
  PageHeader,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  ModalOverlay,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  CloseButton,
  Form,
  FormGroup,
  FormRow,
  FormSection,
  FormSectionTitle,
  Label,
  Input,
  Textarea,
  Select,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  MonoText,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ModalContentLarge,
  ModalBodyScrollable,
} from '../styles/adminShared';

// Page-specific styled components (only those not in shared)
const FormRow3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Light secondary text for helper text
const TertiaryText = styled.span`
  font-size: 12px;
  color: ${theme.textSecondary};
`;

// Address container with proper styling
const AddressContainer = styled.div`
  color: ${theme.textPrimary};
`;

// Table row name cell with logo and text
const NameCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Logo in table
const CellLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: contain;
`;

// Registration info in table name cell
const RegInfo = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
`;

// Table cell text sizes
const TableCellText = styled.span`
  font-size: 12px;
`;

// DPO info display
const DpoDisplay = styled.div`
  font-size: 12px;
`;

// Not set indicator
const NotSetText = styled.span`
  color: ${theme.textSecondary};
`;

// Format role for display (e.g., DATA_FIDUCIARY -> Data Fiduciary)
const formatRole = (role: string | undefined): string => {
  if (!role) return 'Data Fiduciary';
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};



interface FormData {
  // Basic Info
  name: string;
  description: string;
  logo: string;
  internalId: string;
  
  // Government ID (Required)
  uniqueGovtIdType: string;
  uniqueGovtId: string;
  
  // DPDP Compliance
  role: string;
  roleDescription: string;
  
  // Registered Address
  registeredAddressStreet: string;
  registeredAddressCity: string;
  registeredAddressState: string;
  registeredAddressPincode: string;
  registeredAddressCountry: string;
  
  // Contact Info
  contactEmail: string;
  contactPhone: string;
  registrationNumber: string;
  website: string;
  
  // DPO
  dpoName: string;
  dpoEmail: string;
  dpoPhone: string;
  
  // Grievance Officer
  grievanceOfficerName: string;
  grievanceOfficerEmail: string;
  grievanceOfficerPhone: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  logo: '',
  internalId: '',
  uniqueGovtIdType: 'PAN',
  uniqueGovtId: '',
  role: 'DATA_FIDUCIARY',
  roleDescription: '',
  registeredAddressStreet: '',
  registeredAddressCity: '',
  registeredAddressState: '',
  registeredAddressPincode: '',
  registeredAddressCountry: 'India',
  contactEmail: '',
  contactPhone: '',
  registrationNumber: '',
  website: '',
  dpoName: '',
  dpoEmail: '',
  dpoPhone: '',
  grievanceOfficerName: '',
  grievanceOfficerEmail: '',
  grievanceOfficerPhone: '',
};

export const DataCollectors: React.FC = () => {
  const [fiduciaries, setFiduciaries] = useState<DataCollector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingFiduciary, setViewingFiduciary] = useState<DataCollector | null>(null);
  const [editingFiduciary, setEditingFiduciary] = useState<DataCollector | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    loadFiduciaries();
  }, []);

  const loadFiduciaries = async () => {
    try {
      setLoading(true);
      const data = await adminApi.listDataCollectors(1, 25);
      setFiduciaries(data.data || data.content || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data fiduciaries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingFiduciary(null);
    setFormData(initialFormData);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (fiduciary: DataCollector) => {
    setEditingFiduciary(fiduciary);
    setFormData({
      name: fiduciary.name || '',
      description: fiduciary.description || '',
      logo: fiduciary.logo || '',
      internalId: fiduciary.internalId || '',
      uniqueGovtIdType: fiduciary.uniqueGovtIdType || 'PAN',
      uniqueGovtId: fiduciary.uniqueGovtId || '',
      role: fiduciary.role || 'DATA_FIDUCIARY',
      roleDescription: fiduciary.roleDescription || '',
      registeredAddressStreet: fiduciary.registeredAddressStreet || '',
      registeredAddressCity: fiduciary.registeredAddressCity || '',
      registeredAddressState: fiduciary.registeredAddressState || '',
      registeredAddressPincode: fiduciary.registeredAddressPincode || '',
      registeredAddressCountry: fiduciary.registeredAddressCountry || 'India',
      contactEmail: fiduciary.contactEmail || '',
      contactPhone: fiduciary.contactPhone || '',
      registrationNumber: fiduciary.registrationNumber || '',
      website: fiduciary.website || '',
      dpoName: fiduciary.dpoName || '',
      dpoEmail: fiduciary.dpoEmail || '',
      dpoPhone: fiduciary.dpoPhone || '',
      grievanceOfficerName: fiduciary.grievanceOfficerName || '',
      grievanceOfficerEmail: fiduciary.grievanceOfficerEmail || '',
      grievanceOfficerPhone: fiduciary.grievanceOfficerPhone || '',
    });
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleView = async (fiduciary: DataCollector) => {
    try {
      const fullData = await adminApi.getDataCollector(fiduciary.dataCollectorId);
      setViewingFiduciary(fullData);
      setShowViewModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data fiduciary details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!formData.uniqueGovtIdType) {
      setError('Government ID type is required');
      return;
    }
    if (!formData.uniqueGovtId.trim()) {
      setError('Government ID is required');
      return;
    }

    // Validate email and phone fields
    const validationErrors: string[] = [];
    
    const contactEmailError = validateOptionalEmail(formData.contactEmail, 'Contact email');
    if (contactEmailError) validationErrors.push(contactEmailError);
    
    const contactPhoneError = validateOptionalContactPhoneNumber(formData.contactPhone, '+91', 'Contact phone');
    if (contactPhoneError) validationErrors.push(contactPhoneError);
    
    const dpoEmailError = validateOptionalEmail(formData.dpoEmail, 'DPO email');
    if (dpoEmailError) validationErrors.push(dpoEmailError);
    
    const dpoPhoneError = validateOptionalContactPhoneNumber(formData.dpoPhone, '+91', 'DPO phone');
    if (dpoPhoneError) validationErrors.push(dpoPhoneError);
    
    const grievanceEmailError = validateOptionalEmail(formData.grievanceOfficerEmail, 'Grievance officer email');
    if (grievanceEmailError) validationErrors.push(grievanceEmailError);
    
    const grievancePhoneError = validateOptionalContactPhoneNumber(formData.grievanceOfficerPhone, '+91', 'Grievance officer phone');
    if (grievancePhoneError) validationErrors.push(grievancePhoneError);
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    try {
      const request: Partial<DataCollector> = {
        name: formData.name,
        description: formData.description || undefined,
        logo: formData.logo || undefined,
        internalId: formData.internalId || undefined,
        uniqueGovtIdType: formData.uniqueGovtIdType,
        uniqueGovtId: formData.uniqueGovtId,
        role: formData.role || undefined,
        roleDescription: formData.roleDescription || undefined,
        registeredAddressStreet: formData.registeredAddressStreet || undefined,
        registeredAddressCity: formData.registeredAddressCity || undefined,
        registeredAddressState: formData.registeredAddressState || undefined,
        registeredAddressPincode: formData.registeredAddressPincode || undefined,
        registeredAddressCountry: formData.registeredAddressCountry || undefined,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
        registrationNumber: formData.registrationNumber || undefined,
        website: formData.website || undefined,
        dpoName: formData.dpoName || undefined,
        dpoEmail: formData.dpoEmail || undefined,
        dpoPhone: formData.dpoPhone || undefined,
        grievanceOfficerName: formData.grievanceOfficerName || undefined,
        grievanceOfficerEmail: formData.grievanceOfficerEmail || undefined,
        grievanceOfficerPhone: formData.grievanceOfficerPhone || undefined,
      };

      if (editingFiduciary) {
        await adminApi.updateDataCollector(editingFiduciary.dataCollectorId, request);
        setSuccess('Data Fiduciary updated successfully');
      } else {
        await adminApi.createDataCollector(request);
        setSuccess('Data Fiduciary created successfully');
      }

      setShowModal(false);
      loadFiduciaries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save data fiduciary');
    }
  };

  const handleToggleStatus = async (fiduciary: DataCollector) => {
    try {
      if (fiduciary.status === 'ACTIVE') {
        await adminApi.disableDataCollector(fiduciary.dataCollectorId);
      } else {
        await adminApi.enableDataCollector(fiduciary.dataCollectorId);
      }
      loadFiduciaries();
      setSuccess(`Data Fiduciary ${fiduciary.status === 'ACTIVE' ? 'disabled' : 'enabled'} successfully`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  return (
    <AdminShell>
      <PageHeader>
        <PageTitle>Data Fiduciaries</PageTitle>
        <PrimaryButton onClick={handleAdd}>+ Add Data Fiduciary</PrimaryButton>
      </PageHeader>

      {error && !showModal && <AlertMessage $variant="error">{error}</AlertMessage>}
      {success && !showModal && <AlertMessage $variant="success">{success}</AlertMessage>}

      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : fiduciaries.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon>🏢</EmptyStateIcon>
          <EmptyStateTitle>No data fiduciaries found</EmptyStateTitle>
          <EmptyStateText>Create your first data fiduciary to get started.</EmptyStateText>
          <PrimaryButton onClick={handleAdd}>Create Data Fiduciary</PrimaryButton>
        </EmptyStateContainer>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Role</Th>
              <Th>Contact</Th>
              <Th>DPO</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {fiduciaries.map((f) => (
              <Tr key={f.dataCollectorId}>
                <Td>
                  <NameCellContent>
                    {f.logo && (
                      <CellLogo 
                        src={f.logo} 
                        alt="" 
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div>
                      <strong>{f.name}</strong>
                      {f.registrationNumber && (
                        <RegInfo>
                          Reg: {f.registrationNumber}
                        </RegInfo>
                      )}
                    </div>
                  </NameCellContent>
                </Td>
                <Td>
                  <TableCellText>{formatRole(f.role)}</TableCellText>
                </Td>
                <Td>
                  <DpoDisplay>
                    {f.contactEmail && <div>📧 {f.contactEmail}</div>}
                    {f.contactPhone && <div>📞 {f.contactPhone}</div>}
                  </DpoDisplay>
                </Td>
                <Td>
                  <DpoDisplay>
                    {f.dpoName ? (
                      <>
                        <div>{f.dpoName}</div>
                        {f.dpoEmail && <RegInfo>{f.dpoEmail}</RegInfo>}
                      </>
                    ) : (
                      <NotSetText>Not set</NotSetText>
                    )}
                  </DpoDisplay>
                </Td>
                <Td>
                  <StatusBadge $status={f.status || 'ACTIVE'}>
                    {f.status || 'ACTIVE'}
                  </StatusBadge>
                </Td>
                <Td>
                  <ActionButtons>
                    <IconButton $variant="view" onClick={() => handleView(f)}>
                      👁️ View
                    </IconButton>
                    <IconButton $variant="edit" onClick={() => handleEdit(f)}>
                      ✏️ Edit
                    </IconButton>
                    <IconButton
                      $variant={f.status === 'ACTIVE' ? 'danger' : 'success'}
                      onClick={() => handleToggleStatus(f)}
                    >
                      {f.status === 'ACTIVE' ? '⏸️ Disable' : '▶️ Enable'}
                    </IconButton>
                  </ActionButtons>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* View Modal */}
      {showViewModal && viewingFiduciary && (
        <ModalOverlay $show={true}>
          <ModalContentLarge>
            <ModalHeader>
              <ModalTitle>🏢 {viewingFiduciary.name}</ModalTitle>
              <CloseButton onClick={() => setShowViewModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBodyScrollable>
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Basic Info */}
              <FormSection>
                <FormSectionTitle>📋 Basic Information</FormSectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>ID</InfoLabel>
                    <MonoText>{viewingFiduciary.dataCollectorId}</MonoText>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Internal ID</InfoLabel>
                    <InfoValue>{viewingFiduciary.internalId || 'N/A'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Government ID Type</InfoLabel>
                    <InfoValue>{viewingFiduciary.uniqueGovtIdType || 'N/A'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Government ID</InfoLabel>
                    <InfoValue>{viewingFiduciary.uniqueGovtId || 'N/A'}</InfoValue>
                  </InfoItem>
                </InfoGrid>
                {viewingFiduciary.description && (
                  <div style={{ marginTop: '16px' }}>
                    <InfoLabel style={{ display: 'block' }}>Description</InfoLabel>
                    <InfoValue>{viewingFiduciary.description}</InfoValue>
                  </div>
                )}
              </FormSection>
              
              {/* DPDP Compliance */}
              <FormSection>
                <FormSectionTitle>⚖️ DPDP Compliance</FormSectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Role</InfoLabel>
                    <InfoValue>{formatRole(viewingFiduciary.role)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Registration Number</InfoLabel>
                    <InfoValue>{viewingFiduciary.registrationNumber || 'N/A'}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </FormSection>
              
              {/* Contact Info */}
              <FormSection>
                <FormSectionTitle>📞 Contact Information</FormSectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>{viewingFiduciary.contactEmail || 'N/A'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>{viewingFiduciary.contactPhone || 'N/A'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Website</InfoLabel>
                    <InfoValue>{viewingFiduciary.website || 'N/A'}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </FormSection>
              
              {/* Address */}
              {(viewingFiduciary.registeredAddressStreet || viewingFiduciary.registeredAddressCity) && (
                <FormSection>
                  <FormSectionTitle>📍 Registered Address</FormSectionTitle>
                  <AddressContainer>
                    {viewingFiduciary.registeredAddressStreet && <div>{viewingFiduciary.registeredAddressStreet}</div>}
                    <div>
                      {[viewingFiduciary.registeredAddressCity, viewingFiduciary.registeredAddressState, viewingFiduciary.registeredAddressPincode]
                        .filter(Boolean).join(', ')}
                    </div>
                    {viewingFiduciary.registeredAddressCountry && <div>{viewingFiduciary.registeredAddressCountry}</div>}
                  </AddressContainer>
                </FormSection>
              )}
              
              {/* DPO */}
              {viewingFiduciary.dpoName && (
                <FormSection>
                  <FormSectionTitle>🛡️ Data Protection Officer</FormSectionTitle>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Name</InfoLabel>
                      <InfoValue>{viewingFiduciary.dpoName}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>{viewingFiduciary.dpoEmail || 'N/A'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Phone</InfoLabel>
                      <InfoValue>{viewingFiduciary.dpoPhone || 'N/A'}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </FormSection>
              )}
              
              {/* Grievance Officer */}
              {viewingFiduciary.grievanceOfficerName && (
                <FormSection>
                  <FormSectionTitle>📝 Grievance Officer</FormSectionTitle>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Name</InfoLabel>
                      <InfoValue>{viewingFiduciary.grievanceOfficerName}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>{viewingFiduciary.grievanceOfficerEmail || 'N/A'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Phone</InfoLabel>
                      <InfoValue>{viewingFiduciary.grievanceOfficerPhone || 'N/A'}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </FormSection>
              )}
            </div>
            </ModalBodyScrollable>
            <ModalFooter>
              <SecondaryButton onClick={() => setShowViewModal(false)}>Close</SecondaryButton>
              <PrimaryButton onClick={() => { setShowViewModal(false); handleEdit(viewingFiduciary); }}>Edit</PrimaryButton>
            </ModalFooter>
          </ModalContentLarge>
        </ModalOverlay>
      )}

      <ModalOverlay $show={showModal}>
        <ModalContentLarge>
          <ModalHeader>
            <ModalTitle>
              {editingFiduciary ? 'Edit Data Fiduciary' : 'Add New Data Fiduciary'}
            </ModalTitle>
            <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
          </ModalHeader>
          <ModalBodyScrollable>
          {error && <AlertMessage $variant="error">{error}</AlertMessage>}
          
          <Form id="fiduciary-form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <FormSection>
              <FormSectionTitle>📋 Basic Information</FormSectionTitle>
              <FormRow>
                <FormGroup>
                  <Label>Organization Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., HealthCare Plus Hospital"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Internal ID</Label>
                  <Input
                    type="text"
                    value={formData.internalId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, internalId: e.target.value })}
                    placeholder="e.g., ORG-001"
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label>Government ID Type *</Label>
                  <Select
                    value={formData.uniqueGovtIdType}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, uniqueGovtIdType: e.target.value })}
                    required
                  >
                    <option value="PAN">PAN</option>
                    <option value="CIN">CIN (Corporate Identification Number)</option>
                    <option value="GSTIN">GSTIN</option>
                    <option value="TAN">TAN</option>
                    <option value="LLPIN">LLPIN</option>
                    <option value="DARPAN">NGO DARPAN ID</option>
                    <option value="OTHER">Other</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Government ID *</Label>
                  <Input
                    type="text"
                    value={formData.uniqueGovtId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, uniqueGovtId: e.target.value })}
                    required
                    placeholder="e.g., AABCT1234A"
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the organization..."
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <Label>Logo URL</Label>
                  <Input
                    type="url"
                    value={formData.logo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, logo: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Website</Label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* DPDP Compliance */}
            <FormSection>
              <FormSectionTitle>⚖️ DPDP Compliance</FormSectionTitle>
              <FormRow>
                <FormGroup>
                  <Label>Role</Label>
                  <Select
                    value={formData.role}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="DATA_FIDUCIARY">Data Fiduciary</option>
                    <option value="SIGNIFICANT_DATA_FIDUCIARY">Significant Data Fiduciary</option>
                    <option value="DATA_PROCESSOR">Data Processor</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Registration Number</Label>
                  <Input
                    type="text"
                    value={formData.registrationNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registrationNumber: e.target.value })}
                    placeholder="CIN/GSTIN/Registration No."
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label>Role Description</Label>
                <Input
                  type="text"
                  value={formData.roleDescription}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, roleDescription: e.target.value })}
                  placeholder="Description of data processing activities"
                />
              </FormGroup>
            </FormSection>

            {/* Registered Address */}
            <FormSection>
              <FormSectionTitle>📍 Registered Address</FormSectionTitle>
              <FormGroup>
                <Label>Street Address</Label>
                <Input
                  type="text"
                  value={formData.registeredAddressStreet}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registeredAddressStreet: e.target.value })}
                  placeholder="123 Main Street, Building A"
                />
              </FormGroup>
              <FormRow>
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={formData.registeredAddressCity}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registeredAddressCity: e.target.value })}
                    placeholder="Bangalore"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>State</Label>
                  <Input
                    type="text"
                    value={formData.registeredAddressState}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registeredAddressState: e.target.value })}
                    placeholder="Karnataka"
                  />
                </FormGroup>
              </FormRow>
              <FormRow>
                <FormGroup>
                  <Label>Pincode</Label>
                  <Input
                    type="text"
                    value={formData.registeredAddressPincode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registeredAddressPincode: e.target.value })}
                    placeholder="560001"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    value={formData.registeredAddressCountry}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, registeredAddressCountry: e.target.value })}
                    placeholder="India"
                  />
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* Contact Information */}
            <FormSection>
              <FormSectionTitle>📞 Contact Information</FormSectionTitle>
              <FormRow>
                <FormGroup>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="contact@organization.com"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Phone</Label>
                  <Input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="+918012345678"
                  />
                  <TertiaryText>Format: +country code followed by mobile number (e.g., +919876543210)</TertiaryText>
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* Data Protection Officer */}
            <FormSection>
              <FormSectionTitle>🛡️ Data Protection Officer (DPO)</FormSectionTitle>
              <FormRow3>
                <FormGroup>
                  <Label>DPO Name</Label>
                  <Input
                    type="text"
                    value={formData.dpoName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, dpoName: e.target.value })}
                    placeholder="Dr. Rajesh Kumar"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>DPO Email</Label>
                  <Input
                    type="email"
                    value={formData.dpoEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, dpoEmail: e.target.value })}
                    placeholder="dpo@organization.com"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>DPO Phone</Label>
                  <Input
                    type="tel"
                    value={formData.dpoPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, dpoPhone: e.target.value })}
                    placeholder="+918012345678"
                  />
                  <TertiaryText>Format: +country code followed by mobile number (e.g., +918012345678)</TertiaryText>
                </FormGroup>
              </FormRow3>
            </FormSection>

            {/* Grievance Officer */}
            <FormSection>
              <FormSectionTitle>📝 Grievance Officer</FormSectionTitle>
              <FormRow3>
                <FormGroup>
                  <Label>Officer Name</Label>
                  <Input
                    type="text"
                    value={formData.grievanceOfficerName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, grievanceOfficerName: e.target.value })}
                    placeholder="Ms. Priya Sharma"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Officer Email</Label>
                  <Input
                    type="email"
                    value={formData.grievanceOfficerEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, grievanceOfficerEmail: e.target.value })}
                    placeholder="grievance@organization.com"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Officer Phone</Label>
                  <Input
                    type="tel"
                    value={formData.grievanceOfficerPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, grievanceOfficerPhone: e.target.value })}
                    placeholder="+918012345678"
                  />
                  <TertiaryText>Format: +country code followed by mobile number (e.g., +918012345678)</TertiaryText>
                </FormGroup>
              </FormRow3>
            </FormSection>
          </Form>
          </ModalBodyScrollable>
          <ModalFooter>
            <SecondaryButton type="button" onClick={() => setShowModal(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" form="fiduciary-form">
              {editingFiduciary ? 'Update' : 'Create'} Data Fiduciary
            </PrimaryButton>
          </ModalFooter>
        </ModalContentLarge>
      </ModalOverlay>
    </AdminShell>
  );
};

export default DataCollectors;
